// Calm ambience, generated with WebAudio (no audio files): ocean waves, birds by day,
// crickets at night, and a slow soft pad. Off by default; toggled in Settings.

import { localMinute } from "./prefs";

const KEY = "degen-world:ambience";
let on = false;
try {
  on = localStorage.getItem(KEY) === "1";
} catch {
  /* storage blocked: stay off */
}

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let timers: number[] = [];

export function ambienceEnabled() {
  return on;
}

export function setAmbience(v: boolean) {
  on = v;
  try {
    localStorage.setItem(KEY, v ? "1" : "0");
  } catch {
    /* ignore */
  }
  if (v) start();
  else stop();
}

/** Call once after a user gesture (browsers block audio before one). */
export function resumeAmbience() {
  if (on) start();
}

function noiseBuffer(c: AudioContext): AudioBuffer {
  const b = c.createBuffer(1, c.sampleRate * 4, c.sampleRate);
  const d = b.getChannelData(0);
  let last = 0;
  for (let i = 0; i < d.length; i++) {
    last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02; // brown-ish noise: soft, like surf
    d[i] = last * 3.5;
  }
  return b;
}

function start() {
  if (ctx) {
    void ctx.resume();
    return;
  }
  try {
    ctx = new AudioContext();
  } catch {
    return;
  }
  const c = ctx;
  master = c.createGain();
  master.gain.value = 0.0;
  master.gain.linearRampToValueAtTime(0.5, c.currentTime + 3); // fade in gently
  master.connect(c.destination);

  // Waves: looping noise through a lowpass, swelling every ~7 s.
  const surf = c.createBufferSource();
  surf.buffer = noiseBuffer(c);
  surf.loop = true;
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 600;
  const swell = c.createGain();
  swell.gain.value = 0.25;
  const lfo = c.createOscillator();
  lfo.frequency.value = 0.14;
  const lfoAmt = c.createGain();
  lfoAmt.gain.value = 0.18;
  lfo.connect(lfoAmt).connect(swell.gain);
  surf.connect(lp).connect(swell).connect(master);
  surf.start();
  lfo.start();

  // Soft pad: two detuned sines on a slow chord loop.
  const chords = [[220, 277.2, 329.6], [196, 246.9, 293.7], [174.6, 220, 261.6], [196, 246.9, 329.6]];
  const pad = c.createGain();
  pad.gain.value = 0.035;
  pad.connect(master);
  const voices = [0, 1, 2].map(() => {
    const o = c.createOscillator();
    o.type = "sine";
    o.connect(pad);
    o.start();
    return o;
  });
  let ci = 0;
  const nextChord = () => {
    const ch = chords[ci++ % chords.length];
    voices.forEach((o, i) => o.frequency.setTargetAtTime(ch[i], c.currentTime, 1.5));
  };
  nextChord();
  timers.push(window.setInterval(nextChord, 8000));

  // Birds by day, crickets at night (local time).
  const chirp = () => {
    if (!ctx || !master) return;
    const m = localMinute();
    const night = m < 6 * 60 || m > 19 * 60;
    const t = c.currentTime;
    const g = c.createGain();
    g.connect(master);
    const o = c.createOscillator();
    o.connect(g);
    if (night) {
      o.type = "square";
      o.frequency.value = 4200;
      g.gain.setValueAtTime(0, t);
      for (let i = 0; i < 3; i++) {
        g.gain.setValueAtTime(0.008, t + i * 0.09);
        g.gain.setValueAtTime(0, t + i * 0.09 + 0.04);
      }
      o.start(t);
      o.stop(t + 0.3);
    } else {
      o.type = "sine";
      const f = 2400 + Math.random() * 1600;
      o.frequency.setValueAtTime(f, t);
      o.frequency.exponentialRampToValueAtTime(f * 1.5, t + 0.08);
      o.frequency.exponentialRampToValueAtTime(f * 0.9, t + 0.16);
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.03, t + 0.02);
      g.gain.linearRampToValueAtTime(0, t + 0.18);
      o.start(t);
      o.stop(t + 0.2);
    }
  };
  timers.push(window.setInterval(() => Math.random() < 0.55 && chirp(), 2300));
}

function stop() {
  timers.forEach((t) => window.clearInterval(t));
  timers = [];
  if (ctx) void ctx.close();
  ctx = null;
  master = null;
}
